// components/CheckoutPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config/config';
import ShareableNav from "../components/ShareableNav";
// Components (same as ProductView)
import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";      // For cart modal in Nav
import { CreditCard, Smartphone, Lock, Check, MapPin, User, Mail, Phone, ShoppingBag, ArrowLeft,  Clock, Send, MessageSquare, HelpCircle, CheckCircle } from 'lucide-react';

const CACHE_KEY = 'checkout_page_cache';
const CACHE_TTL = 24*60*60 * 1000; // 1 minute

const CheckoutPage = () => {
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const [settings, setSettings]=useState([]);
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');

  // Layout configs – start null, filled from cache instantly
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [checkoutConfig, setCheckoutConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [cartItems, setCartItems] = useState([]); 

  const defaultConfig = {
    activeDesign: 'side-by-side', // 'side-by-side', 'wizard', 'compact'
    title: 'Checkout',
    shippingTitle: 'Shipping Information',
    paymentTitle: 'Payment Method',
    reviewTitle: 'Review Your Order',
    emptyCartMessage: 'Your cart is empty',
    continueShoppingText: 'Continue Shopping',
    checkoutText: 'Complete Order',
    customStyles: {
      background: 'bg-gray-50',
      textColor: 'text-gray-900',
      buttonBg: 'bg-gray-900',
      buttonText: 'text-white',
      accentColor: 'text-green-600',
    },
  };

  const finalConfig = { ...defaultConfig, ...checkoutConfig };
  const {
    activeDesign,
    title,
    shippingTitle,
    paymentTitle,
    reviewTitle,
    emptyCartMessage,
    continueShoppingText,
    checkoutText,
    customStyles,
  } = finalConfig;

  // ────────────────────────────────────────────────
  // Cache Helpers
  // ────────────────────────────────────────────────
  const getCached = () => {
    const item = localStorage.getItem(CACHE_KEY);
    if (!item) return null;
    try {
      const { data, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp < CACHE_TTL) return data;
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  const setCached = (data) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  };

  // ────────────────────────────────────────────────
  // Load Layout → Cache FIRST, then background API refresh
  // ────────────────────────────────────────────────
 
 const fetchUser = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${config.API_URL}/api/pullusers/${token}`);
      if (res.data.status === 0) {
        // handle deactivated
      } else {
        setSettings(res.data.settings);
       
        localStorage.setItem('userInfor', JSON.stringify(res.data.user));
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('userInfor');
      //setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [token]);

const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email Us',
      value: settings?.email,
      description: 'Send us an email anytime',
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      icon: Phone,
      label: 'Call Us',
      value: settings?.contact,
      description: settings?.work_hours,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: settings?.address,
      description: 'Kenya, East Africa',
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      icon: Clock,
      label: 'Working Hours',
      value: settings?.work_hours,
      description: 'Sat: 9:00 AM - 4:00 PM',
      color: 'text-orange-600',
      bg: 'bg-orange-100'
    }
  ];

  const inquiryTypes = [
    {
      value: 'general',
      icon: MessageSquare,
      label: 'General Inquiry',
      description: 'Questions about our services'
    },
    {
      value: 'support',
      icon: HelpCircle,
      label: 'Customer Support',
      description: 'Need help with an order'
    },
    {
      value: 'sales',
      icon: ShoppingBag,
      label: 'Sales',
      description: 'Interested in bulk orders'
    }
  ];

  const faqz = [
    {
      question: 'What are your shipping options?',
      answer: 'We offer standard (3-5 days) and express (1-2 days) shipping across Kenya.'
    },
    {
      question: 'How can I track my order?',
      answer: 'You will receive a tracking number via email once your order ships.'
    },
    {
      question: 'What is your return policy?',
      answer: 'We offer 30-day hassle-free returns on all products.'
    }
  ];

const [faqs,setAllFaqs]=useState([]);
  // Fetch ALL available FAQs for modal
  const fetchAllFaqs = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/pullFaqs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllFaqs(response.data.faqs || []);
    } catch (err) {
      console.error("Failed to fetch all FAQs:", err);
    }
  };

  useEffect(() => {
    if (token) fetchAllFaqs();
  }, [token]);



  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // Safe config handling
  const actualCheckoutConfig = 
    checkoutConfig?.checkoutSettings && typeof checkoutConfig.checkoutSettings === 'object' && !Array.isArray(checkoutConfig.checkoutSettings)
      ? checkoutConfig.checkoutSettings
      : (typeof checkoutConfig === 'object' && !Array.isArray(checkoutConfig) ? checkoutConfig : {});

  const checkoutDefault = {
    activeDesign: 'side-by-side',
    devMode: false,
    customStyles: {},
  };

  const finalCheckoutConfig = { ...checkoutDefault, ...actualCheckoutConfig };
  const { activeDesign: checkoutDesign, devMode: checkoutDevMode, customStyles: checkoutStyles } = finalCheckoutConfig;

  const containerStyles = {
    background: checkoutDevMode && checkoutStyles.background ? checkoutStyles.background : undefined,
    color: checkoutDevMode && checkoutStyles.textColor ? checkoutStyles.textColor : undefined,
  };

 

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
  const shipping = subtotal > 100 ? 0 : 10;
  const tax = subtotal * 0.16;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };



  // Design 1: Side-by-Side Layout
  const Design1 = () => (
     <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Get in Touch</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Have a question or need assistance? We're here to help. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-12 mb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {contactInfo.map((info, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className={`w-12 h-12 ${info.bg} rounded-lg flex items-center justify-center mb-4`}>
                <info.icon className={`w-6 h-6 ${info.color}`} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{info.label}</h3>
              <p className="text-gray-900 font-medium mb-1">{info.value}</p>
              <p className="text-sm text-gray-600">{info.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
          
           

                 <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Answers</h3>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="pb-4 border-b border-gray-200 last:border-0 last:pb-0">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">{faq.question}</h4>
                    <p className="text-sm text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
             
            </div>
<br/>

            {/* Map Section 
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Visit Our Store</h2>
            <p className="text-gray-600">Come see us in person at our location</p>
          </div>
          
          
          <div className="aspect-video md:aspect-[21/9] bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Interactive Map</p>
              <p className="text-sm text-gray-500">123 Main Street, Nairobi, Kenya</p>
            </div>
          </div>
        </div>
      </div>*/}
            
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick FAQs */}
         

            {/* Live Chat CTA */}
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl shadow-lg p-6">
              <MessageSquare className="w-10 h-10 mb-4" />
              <h3 className="text-xl font-bold mb-2">Need Immediate Help?</h3>
              <p className="text-blue-100 mb-4 text-sm">
                Chat with our support team now and get instant answers to your questions.
              </p>
              <button onClick={(e)=>alert('Livechat feature coming soon!')} className="w-full bg-white text-blue-600 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                Start Live Chat
              </button>
              <p className="text-xs text-blue-100 mt-3 text-center">
                Average response time: 2 minutes
              </p>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
              <p className="text-sm text-gray-600 mb-4">
                Stay connected with us on social media for updates and offers.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href={settings?.facebook}
                  target="_blank"
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-blue-500 hover:text-white rounded-lg transition-all font-medium text-sm"
                >
                  TikTok
                </a>
                <a
                target="_blank"
                 href={settings?.twitter}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-blue-400 hover:text-white rounded-lg transition-all font-medium text-sm"
                >
                Whatsapp
                </a>
                <a
                target="_blank"
                 href={settings?.instagram}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-pink-500 hover:text-white rounded-lg transition-all font-medium text-sm"
                >
                  Instagram
                </a>
               
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-900 text-white rounded-2xl shadow-lg p-6">
              <Clock className="w-10 h-10 mb-4" />
              <h3 className="text-xl font-bold mb-4 text-white">Business Hours</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">{settings?.work_hours}</span>
               
                </div>
                <div className="flex justify-between">
                
                </div>
                <div className="flex justify-between">
                 
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );

  // Design 2: Multi-Step Wizard (Guided Flow)
  const Design2 = () => (
    <div className={`min-h-screen ${customStyles.background}`}>
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex justify-between mb-12 max-w-md mx-auto">
          {['Shipping', 'Payment', 'Review'].map((label, index) => (
            <div key={label} className="flex-1 text-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto font-bold text-lg transition-all ${
                  step > index + 1
                    ? 'bg-green-600 text-white'
                    : step === index + 1
                    ? 'bg-gray-900 text-white ring-4 ring-gray-200'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {step > index + 1 ? <Check size={24} /> : index + 1}
              </div>
              <p className={`mt-3 font-medium ${step === index + 1 ? 'text-gray-900' : 'text-gray-500'}`}>{label}</p>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {step === 1 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{shippingTitle}</h2>
              {/* Shipping form */}
              <button
                onClick={() => setStep(2)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Continue to Payment
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{paymentTitle}</h2>
              {/* Payment selection */}
              <button
                onClick={() => setStep(3)}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                Review & Confirm
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-3xl font-bold mb-8">{reviewTitle}</h2>
              {/* Summary */}
              <button
                onClick={handleSubmit}
                className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
              >
                {checkoutText} - ${total.toFixed(2)}
              </button>
            </>
          )}
        </div>

        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="mt-6 text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <ArrowLeft size={20} /> Back
          </button>
        )}
      </div>
    </div>
  );

  // Design 3: Compact Mobile-First
  const Design3 = () => (
    <div className={`min-h-screen ${customStyles.background} py-8 px-4`}>
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <h2 className="text-3xl font-bold mb-8 text-center">{title}</h2>

        {/* Compact shipping + payment + summary */}
        <div className="space-y-8">
          {/* Shipping */}
          <div>
            <h3 className="text-xl font-bold mb-4">{shippingTitle}</h3>
            {/* Compact form */}
          </div>

          {/* Payment */}
          <div>
            <h3 className="text-xl font-bold mb-4">{paymentTitle}</h3>
            {/* Compact payment */}
          </div>

          {/* Summary */}
          <div className="border-t pt-6">
            <h3 className="text-xl font-bold mb-4">Summary</h3>
            <div className="flex justify-between font-bold text-xl">
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className={`w-full py-5 rounded-xl font-bold text-lg ${customStyles.buttonBg} ${customStyles.buttonText}`}
          >
            {checkoutText}
          </button>
        </div>
      </div>
    </div>
  );

  const renderCheckoutDesign = () => {
    switch (checkoutDesign) {
      case 'side-by-side': return <Design1 />;
      case 'wizard': return <Design2 />;
      case 'compact': return <Design3 />;
      default: return <Design1 />;
    }
  };

 

  return (
    <div className="flex flex-col min-h-screen relative">
      {/* Flash Bar */}
    <style>{`
       @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
     html,body{
       font-family: ${config.FONT_FAMILY}, sans-serif !important;
     }
      `}
      </style>
     <ShareableNav/>
    
      

     
      <main className="flex-grow" style={containerStyles}>
      
        {renderCheckoutDesign()}
      </main>
       

      {/* Footer */}
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default CheckoutPage;